import { lazy, Suspense, type ReactElement } from 'react'
import { Navigate, Outlet } from 'react-router-dom'
import type { ShellPage } from '../newui/nav'
import { useAuthStore } from '../stores/authStore'

/**
 * Routing by users.ui_version (redesign F2; spec f2_ui_version_shell.md §4).
 * These render inside ProtectedRoute, so the user is already loaded. The new shell is a
 * lazy chunk: users on the old UI never download its code, CSS or fonts.
 */
const NewShell = lazy(() => import('../newui/NewShell'))

function useUiVersion() {
  return useAuthStore((s) => s.user?.uiVersion ?? 'old')
}

/** A page that exists only in the new UI. Users on old go to /home. */
export function NewShellPage({ page }: { page: ShellPage }) {
  if (useUiVersion() !== 'new') return <Navigate to="/home" replace />
  return (
    <Suspense fallback={<div className="min-h-screen bg-stone-50" aria-label="Loading" />}>
      <NewShell page={page} />
    </Suspense>
  )
}

/** A path both UIs have (/home, /library): the old page or the new shell's. */
export function ByVersion({ old, page }: { old: ReactElement; page: ShellPage }) {
  return useUiVersion() === 'new' ? <NewShellPage page={page} /> : old
}

/** Layout for pages only the old UI has. Users on new go to /home. */
export function OldOnly() {
  return useUiVersion() === 'new' ? <Navigate to="/home" replace /> : <Outlet />
}
